import React from 'react'
import {useParams, Link} from 'react-router-dom'
import useDashboard from '../hooks/useDashboard'
import {formatNumber} from '../lib/format'
import invert from 'invert-color'
import {pieClasses, PieChart} from '@mui/x-charts/PieChart'
import useDocument from '../hooks/useDocument'

const Stat = ({value, title, color, valueFormatter, href, caption}) => {
  color = color ? `-${color}` : ''
  const isHighlighted = value > 0
  const formattedValue = valueFormatter ? valueFormatter(value) : value
  return (
    <div className={`box is-shadowless has-text-centered ${isHighlighted ? '' : `has-background${color}-light`}`}>
      <p className={`title is-5 m-0 has-text${color} ${isHighlighted ? '' : 'has-text-grey'}`}>
        {href && value ? (
          <Link to={href} style={{textDecoration: 'none'}}>
            {formattedValue}
          </Link>
        ) : (
          formattedValue
        )}
      </p>
      <p className={`subtitle is-6 m-0 mt-1 ${isHighlighted ? 'has-text-weight-semibold' : ''}`}>
        <span title={caption} style={caption ? {cursor: 'help'} : null}>
          {title}
        </span>
      </p>
    </div>
  )
}

const Tag = ({href, children, color, className}) => {
  let style = {textDecoration: 'none'}
  color ||= 'f5f5f5'
  style.backgroundColor = `#${color}`
  style.color = invert(`#${color}`, true)
  return (
    <Link to={href} style={style} className={`tag ${className}`}>
      {children}
    </Link>
  )
}

const formatMoney = value => `$${formatNumber(value)}`
const formatCount = value => `${value} accounts`

export default () => {
  const {organisationID} = useParams()
  const {dashboard} = useDashboard(organisationID)
  useDocument({title: 'Dashboard'})

  const billed_total = dashboard.totalBalanced + dashboard.totalOwing + dashboard.totalOverdue + dashboard.totalInCredit

  return (
    <section className='section'>
      <div className='container'>
        <div className='columns is-multiline is-variable is-2'>
          <div className='column is-half'>
            <article className='message is-danger'>
              <div className='message-header'>Revenue leaks</div>
              <div className='message-body p-3'>
                <div className='columns is-multiline is-variable is-1'>
                  <div className='column is-half'>
                    <Stat
                      value={dashboard.needToBeBilledAccounts}
                      href={`/orgs/${organisationID}/accounts/active?dashboard=need_to_be_billed_accounts`}
                      title='need to be billed'
                      caption="NEED TO BE BILLED: Accounts have an active service and are not being billed. Excludes 'ACCOUNT CANCELLED', 'Marae by CIP' and various free groups."
                      color='danger'
                      valueFormatter={formatCount}
                    />
                  </div>
                  <div className='column is-half'>
                    <Stat
                      value={dashboard.recoverableMoney}
                      href={`/orgs/${organisationID}/accounts/active?dashboard=recoverable_money&sortBy=balance&sortDirection=asc&sortLabel=Balance`}
                      title='is overdue'
                      caption='OVERDUE: Accounts are being billed, and are overdue.'
                      color='danger'
                      valueFormatter={formatMoney}
                    />
                  </div>
                  <div className='column is-half'>
                    <Stat
                      value={dashboard.shouldNotBeBilledAccounts}
                      href={`/orgs/${organisationID}/accounts/active?dashboard=should_not_be_billed_accounts`}
                      title='billed without a service'
                      color='danger'
                      valueFormatter={formatCount}
                    />
                  </div>
                  <div className='column is-half'>
                    <Stat
                      value={dashboard.badDebt}
                      href={`/orgs/${organisationID}/accounts/active?dashboard=bad_debt`}
                      title='is bad debt'
                      caption='BAD DEBT: Accounts are not being billed, are owing, and we may write it off or pass it to collections.'
                      color='danger'
                      valueFormatter={formatMoney}
                    />
                  </div>
                </div>
              </div>
            </article>
          </div>
          <div className='column is-half'>
            <article className='message is-warning'>
              <div className='message-header'>Payment management</div>
              <div className='message-body p-3'>
                <div className='columns is-multiline is-variable is-2'>
                  <div className='column is-half'>
                    <Stat
                      value={dashboard.creditHeld}
                      href={`/orgs/${organisationID}/accounts/active?dashboard=credit_held`}
                      title='total credit held'
                      caption='TOTAL CREDIT: Credit across all active accounts (billed and unbilled).'
                      color='warning'
                      valueFormatter={formatMoney}
                    />
                  </div>
                  <div className='column is-half'>
                    <Stat
                      value={dashboard.overpayingAccounts}
                      href={`/orgs/${organisationID}/accounts/active?dashboard=overpaying_accounts`}
                      title='are overpaying'
                      caption='OVERPAYING: Accounts not billed in the last 2 months, but still receiving payments.'
                      color='warning'
                      valueFormatter={formatCount}
                    />
                  </div>
                  <div className='column is-half'>
                    <Stat
                      value={dashboard.refundableMoney}
                      href={`/orgs/${organisationID}/accounts/active?dashboard=refundable_money`}
                      title='for potential refund'
                      caption='POTENTIAL REFUND: Accounts are not being billed, are in credit, and may be eligible for a refund.'
                      color='warning'
                      valueFormatter={formatMoney}
                    />
                  </div>
                </div>
              </div>
            </article>
          </div>
          <div className='column is-one-third'>
            <article className='message is-info'>
              <div className='message-header'>
                <span>Billed customers</span>
                <div className='tags'>
                  <Tag
                    href={`/orgs/${organisationID}/accounts/active?dashboard=billed_fibre_accounts`}
                    color='18C7FF'
                    className='is-small'>
                    Fibre: {dashboard.billedFibreAccounts}
                  </Tag>
                  <Tag
                    href={`/orgs/${organisationID}/accounts/active?dashboard=billed_wireless_accounts`}
                    color='EDE60F'
                    className='is-small'>
                    Wireless: {dashboard.billedWirelessAccounts}
                  </Tag>
                </div>
              </div>
              <div className='message-body p-3 has-text-centered'>
                <PieChart
                  series={[
                    {
                      arcLabel: item => `${formatNumber((item.value / billed_total) * 100)}%`,
                      arcLabelRadius: '55%',
                      data: [
                        {id: 0, value: dashboard.totalBalanced, label: 'Balanced', color: '#48c774'},
                        {id: 1, value: dashboard.totalOwing, label: 'Owing', color: '#EAAC3C'},
                        {id: 2, value: dashboard.totalOverdue, label: 'Overdue', color: '#D22043'},
                        {id: 3, value: dashboard.totalInCredit, label: 'In credit', color: 'gray'}
                      ],
                      valueFormatter: item => `${item.value} accounts`,
                      highlightScope: {fade: 'global'},
                      faded: {additionalRadius: -10, color: 'gray'}
                    }
                  ]}
                  sx={{
                    [`& .${pieClasses.arcLabel}`]: {
                      fontWeight: 'bold',
                      fontSize: '0.9rem'
                    }
                  }}
                  height={200}
                />
              </div>
            </article>
          </div>
          <div className='column is-5'>
            <article className='message'>
              <div className='message-header'>Group watch</div>
              <div className='message-body p-3'>
                <table className='table is-striped is-fullwidth'>
                  <tbody>
                    {dashboard.groups?.map((group, i) => (
                      <tr key={i}>
                        <td className='has-text-weight-semibold'>{formatCount(group.numOfActiveAccounts)}</td>
                        <td>
                          <Tag
                            color={group.colour}
                            href={`/orgs/${organisationID}/accounts/active?includeGroupIds=${group.id}`}>
                            {group.name}
                          </Tag>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </article>
          </div>
          <div className='column is-3'>
            <article className='message is-light'>
              <div className='message-header'>Archived</div>
              <div className='message-body p-3'>
                <div className='columns is-multiline is-variable is-1'>
                  <div className='column is-full'>
                    <Stat
                      value={dashboard.writtenOffMoney}
                      href={`/orgs/${organisationID}/accounts/archived?dashboard=written_off_money`}
                      title='was written off'
                      valueFormatter={formatMoney}
                    />
                  </div>
                  <div className='column is-full'>
                    <Stat
                      value={dashboard.unrecoveredMoney}
                      href={`/orgs/${organisationID}/accounts/archived?dashboard=unrecovered_money`}
                      title='was unrecovered'
                      valueFormatter={formatMoney}
                    />
                  </div>
                </div>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>
  )
}
